import { useState } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/da243sjk8r5te5mqsg30';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐检测：命中则按正常提交反馈处理，不向表单地址发送任何内容
    const honeypot = (formData.get('website_alt')?.toString() || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    setSubmitting(true);
    setFormError('');
    setStatus('idle');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt' && value) payload.append(key, value.toString());
    });

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const code = parsed?.code;
      const msg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        responseText;

      if (res.ok && code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(typeof msg === 'string' && msg ? msg : '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form="feedback"
      onSubmit={handleSubmit}
      className="space-y-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-900">
            你的称呼
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-900">
            邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="你的邮箱"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div>
        <label htmlFor="type" className="mb-1.5 block text-sm font-medium text-foreground-900">
          留言类型
        </label>
        <select
          id="type"
          name="type"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          <option value="作品推荐">作品推荐</option>
          <option value="意见反馈">意见反馈</option>
          <option value="剧情勘误">剧情勘误</option>
          <option value="合作咨询">合作咨询</option>
        </select>
      </div>

      <div>
        <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-900">
          留言内容
        </label>
        <textarea
          id="message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="想推荐哪部番？或对分集剧情有什么建议？"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
        <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
      </div>

      {/* 反垃圾蜜罐字段 */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="anti-spam-field"
      />

      <div className="flex flex-col items-stretch gap-3 pt-1 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin" /> 提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill" /> 提交留言
            </>
          )}
        </button>
        <a
          href="mailto:contact@xiuxiu-dongman.com"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-200"
        >
          <i className="ri-mail-line" /> 直接发邮件
        </a>
      </div>

      {status === 'success' && (
        <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm font-medium text-secondary-900">
          <i className="ri-checkbox-circle-fill" /> 收到！感谢你的留言，我们会尽快查看。
        </p>
      )}
      {status === 'error' && formError && (
        <p className="flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm font-medium text-primary-800">
          <i className="ri-error-warning-fill" /> {formError}
        </p>
      )}
    </form>
  );
}